import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatMenu, MatMenuModule } from "@angular/material/menu";
import { UserManagementService } from '../user-management.service';
import { SimpleCodeValue } from '../../../core/models/code-value.model';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { environment } from '../../../../environments/environment';
import * as CryptoJS from 'crypto-js';
import Swal from 'sweetalert2';
import { AuthContext } from '../../../auth/services/auth.context';
import { FindLocationDialogComponent, FindLocationDialogData, FindLocationDialogResult } from './find-location-dialog/find-location-dialog.component';
import { AssignedLocation, LocationItem, ResidentFormData, ResidentDetail } from '../../../core/models/location.model';
import { ResidentPayload } from '../../../core/models/resident.model';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import { LookupItem } from '../../../core/services/lookup.service';
import { PublicService } from '../../../core/services/public.service';
import { ManageResidentsService, ResidentSearchRequest } from '../../../core/services/resident.service';

export function noSpecialCharsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Allow empty values → other validators (like required) will handle this
    if (value === null || value === '') {
      return null;
    }

    // allow only letters and spaces
    const valid = /^[a-zA-Z\s]*$/.test(value);

    return valid ? null : { invalidChars: true };
  };
}
@Component({
  selector: 'app-manage-residents',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatTableModule,
    MatCheckboxModule,
    CommonModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatSelectModule,
    MatExpansionModule,
    MatIconModule,
    MatDialogModule,
    FormsModule,
    MatTab,
    MatTabGroup,
    MatMenu,
    MatMenuModule,
    MatSnackBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './manage-residents.component.html',
  styleUrl: './manage-residents.component.scss'
})


export class ManageResidentsComponent implements OnInit, OnDestroy {

  searchLength: number = 0; // Length for this.paginator (search results)
  assignedLength: number = 0;

  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  @ViewChild('tabGroup') tabGroup: any;
  @ViewChild(MatTable) table!: MatTable<LocationItem>;
  @ViewChild('assignedPaginator') assignedPaginator!: MatPaginator;

  private destroy$ = new Subject<void>();

  residentForm!: FormGroup;
  loginForm!: FormGroup;
  searchForm!: FormGroup;
  newLocationForm!: FormGroup;
  selectedTabIndex = 0;

  hideCurrentPassword = true;

  residentTypes: LookupItem[] = [];
  residentPriorities: LookupItem[] = [];
  selectedResidentType: string = '';
  searchResults: LocationItem[] = [];
  dataSource = new MatTableDataSource<LocationItem>(this.searchResults);
  @ViewChild(MatPaginator) paginator: MatPaginator | null = null;
  // pagination state
  length = 0;
  pageIndex = 0;
  pageSize = 10;

  assignedLocationsArray: AssignedLocation[] = [];
  assignedLocations = new MatTableDataSource<AssignedLocation>(this.assignedLocationsArray);
  displayedColumns: string[] = ['areaName', 'QuarterNo', 'blockNumber', 'locationName', 'locationAssignedTo', 'actions', 'status'];

  displayedSearchColumns: string[] = ['residentType', 'residentName', 'mobileNumber', 'emailAddress', 'locationName', 'userName', 'accountActive', 'view'];

  showFormTabs = false;
  areas: SimpleCodeValue[] = [];
  userNameExists: boolean = false;
  preFilled: boolean = false;
  residentID: string | number | null = null;
  originalUsername: string | null = null;
  previousResidentType: string | null = null;
  locationTypes: LookupItem[] = [];
  isLoading = false; // Initially true to show the loader while data is being fetched

  constructor(
    private fb: FormBuilder,
    private ManageResidentsService: ManageResidentsService,
    private cdr: ChangeDetectorRef,
    private UserManagementService: UserManagementService,
    private publicService: PublicService,
    private authContext: AuthContext
  ) { }

  ngOnInit(): void {
    const lookupValues = this.authContext.getLookups();
    this.residentTypes = lookupValues['RESIDENT_TYPE'] || [];
    this.residentPriorities = lookupValues['RESIDENT_PRIORITY_TYPE'] || [];
    this.locationTypes = lookupValues['LOCATION_TYPE'] || [];

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    this.residentForm = this.fb.group({
      residentType: ['', Validators.required],
      residentPriority: ['', Validators.required],
      rank: ['', noSpecialCharsValidator()],
      mobileNo: ['', [Validators.required, Validators.pattern('^[0-9]{10}$'), Validators.pattern(/^(?!.*(\d)\1{9})([1-9]\d{9})$/)]],
      altermobileNo: ['', [Validators.pattern(/^(?!.*(\d)\1{9})([1-9]\d{9})$/)]],
      firstName: ['', [Validators.required, Validators.minLength(2), noSpecialCharsValidator()]],
      emailAddress: ['', [Validators.required, Validators.pattern(emailRegex)]],
      lastName: ['', noSpecialCharsValidator()],
      allowEmail: [true],
      allowSMS: [true],
    });

    this.newLocationForm = this.fb.group({
      newArea: ['', Validators.required],
      locationCode: [''],
      qtrNumber: ['', Validators.required],
      blockNumber: ['', Validators.required],
      locationDetails: [''],
      locationName: [''],
      phoneNumber: ['', Validators.pattern('^[0-9]{10}$')],
      locationType: ['', Validators.required]
    });

    this.newLocationForm.get('locationType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateLocationCode());
    this.newLocationForm.get('qtrNumber')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateLocationCode());
    this.newLocationForm.get('blockNumber')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateLocationCode());

    this.loginForm = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(50), Validators.pattern('^[a-zA-Z0-9@._]+$')]],
      password: ['', [
        Validators.minLength(8),
        Validators.maxLength(12),
        Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$'),
      ],],
      loginAccountEnabled: [true]
    });
    this.updatePasswordValidation();

    this.searchForm = this.fb.group({
      areaId: [''],
      residentName: [''],
      qtrNumber: [''],
      areaName: [''],
      locatioName: [''],
      mobileNumber: ['', [Validators.pattern(/^\d{10}$/)]],
      blockNumber: [''],
      email: ['', [Validators.email]],
    });

    this.showFormTabs = false;
    this.assignedLocations.paginator = this.paginator;
    this.getUserZoneAreas();
  }

  onReset() {
    this.searchForm.reset({});
    this.dataSource.data = [];
  }

  getResidentTypeLookUps(lookupCode: string) {
    const lookupValues = this.residentTypes;
    return lookupValues.find((item) => item.lookupCode === lookupCode)?.lookupValue;
  }

  onResidentTypeChange(event: { value: string }) {
    this.selectedResidentType = event.value;
    console.log('Resident Type Changed to:', this.selectedResidentType);

    const newType = event.value;

    const activeLocations = this.assignedLocations.data.filter(
      (loc: AssignedLocation) => loc.status === 'Assigned' || loc.status === 'Newly Added'
    );

    if (this.previousResidentType === 'GROUP' && newType === 'IND' && activeLocations.length > 1) {
      Swal.fire({
        title: 'Warning!',
        text: 'Individual resident types can only have one location. Please reduce the number of assigned locations to one before changing the resident type.',
        icon: 'warning',
        confirmButtonText: 'Okay'
      }).then((result) => {
        if (result.isConfirmed) {
          console.log(this.previousResidentType, newType);
          this.previousResidentType = 'GROUP';
          this.residentForm.get('residentType')?.setValue('GROUP');
          this.tabGroup.selectedIndex = 1;
        }
      });
    }
    this.previousResidentType = newType;
  }

  updateLocationCode() {
    const locationType = this.newLocationForm.get('locationType')?.value || '';
    const qtrNumber = this.newLocationForm.get('qtrNumber')?.value || '';
    const blockNumber = this.newLocationForm.get('blockNumber')?.value || '';

    if (locationType || qtrNumber || blockNumber) {
      this.newLocationForm.get('locationCode')?.setValue(
        `${locationType}${qtrNumber ? '-' + qtrNumber : ''}${blockNumber ? '/' + blockNumber : ''}`,
        { emitEvent: false }
      );
    }
  }

  onCreateNewUser() {
    this.resetForm();
    this.showFormTabs = true;
    this.selectedTabIndex = 0;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    const filteredValue = value.replace(/[^0-9]/g, '');

    if (value !== filteredValue) {
      input.value = filteredValue;
    }
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 3 * 1000 }); // 3 seconds
  }

  openLocationPopup(): void {
    const dialogData: FindLocationDialogData = {
      areas: this.areas,
      currentAssignedLocations: this.preFilled ? this.assignedLocationsArray : [],
      selectedResidentType: this.selectedResidentType,
      locationTypes: this.locationTypes
    };

    const dialogRef = this.dialog.open(FindLocationDialogComponent, {
      width: '80%',
      maxWidth: '100%',
      height: '90%',
      maxHeight: '100%',
      data: dialogData,
      disableClose: false,
      autoFocus: false
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: FindLocationDialogResult) => {
      if (result) {
        // Update assigned locations with the result from dialog
        this.assignedLocationsArray = result.assignedLocations;
        this.assignedLocations.data = [...this.assignedLocationsArray];
        this.assignedLocations.paginator = this.assignedPaginator;
        console.log('Updated Assigned Locations from dialog:', this.assignedLocations.data);
      }
    });
  }

  removeLocation(id: string) {
    console.log('Removing location with id:', id);

    this.assignedLocationsArray = this.assignedLocationsArray.map(loc =>
      loc.id === id ? { ...loc, status: 'Deleted' } : loc
    );

    // Update the data source data instead of recreating
    this.assignedLocations.data = [...this.assignedLocationsArray];

    console.log('Updated Assigned Locations:', this.assignedLocations.data);
  }

  getSelectedLocationsCount(): number {
    return this.assignedLocationsArray.filter((location) => location.status !== 'Deleted').length;
  }

  onSubmit(): void {
    if (this.residentForm.invalid || this.loginForm.invalid) {
      this.residentForm.markAllAsTouched();
      this.loginForm.markAllAsTouched();
      this.openSnackBar("Please fill out all required fields before submitting.", "Okay");
      return;
    }

    const locationList = this.assignedLocations?.filteredData || [];
    if (locationList.length === 0) {
      this.openSnackBar("Location details are not added. Please add at least one location.", "Okay");
      return;
    }

    let finalData: ResidentFormData;
    const formData = { ...this.residentForm.value, ...this.loginForm.getRawValue() };
    finalData = {
      ...formData,
      assignedLocations: this.assignedLocations
    };

    let ivBase64: string | null = null;
    let encryptedUsername: string | null = null;
    let encryptedPassword: string | null = null;

    if (finalData && (finalData['userName'] || finalData['password'])) {
      const secret = environment.ivBase64Secret;
      const sha256Key = CryptoJS.SHA256(secret);
      const iv = CryptoJS.lib.WordArray.random(16);
      ivBase64 = CryptoJS.enc.Base64.stringify(iv);

      const rawUser = finalData['userName'];
      const rawPass = finalData['password'];

      if (rawUser) {
        encryptedUsername = CryptoJS.AES.encrypt(String(rawUser), sha256Key, { iv: iv }).toString();
      }

      if (rawPass) {
        encryptedPassword = CryptoJS.AES.encrypt(String(rawPass), sha256Key, { iv: iv }).toString();
      }
    }

    let payload: ResidentPayload = {
      residentId: this.residentID || null,
      rank: finalData['rank'],
      residentFirstName: finalData['firstName'],
      residentLastName: finalData['lastName'],
      phoneNumber: finalData['mobileNo'],
      mobileNumber: finalData['mobileNo'],
      alternateMobileNumber: finalData['altermobileNo'],
      ivrLanguage: 'English',
      emailAddress: finalData['emailAddress'],
      allowSmsFlag: finalData['allowSMS'] ? 'Y' : 'N',
      allowEmailFlag: finalData['allowEmail'] ? 'Y' : 'N',
      residentType: finalData['residentType'],
      residentPriority: finalData['residentPriority'],
      allowSms: finalData['allowSMS'],
      allowEmail: finalData['allowEmail'],
      fullName: `${finalData['firstName'] || ''} ${finalData['lastName'] || ''}`.trim(),
      residentPic: null,
      loginRequest: {
        userName: encryptedUsername,
        password: encryptedPassword,
        instance: ivBase64
      },
      loginAccountEnabled: finalData['loginAccountEnabled'],
      locationAllocationList: finalData['assignedLocations']?.filteredData
        ?.filter((location: AssignedLocation) => location.status !== 'Deleted')
        .map((location: AssignedLocation) => ({
          locationId: location.locationId || null,
          allocationId: location.allocationId || null
        })) || []
    };

    const apiCall = this.preFilled
      ? this.ManageResidentsService.editResident(payload)
      : this.ManageResidentsService.submitResident(payload);

    apiCall.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        if (response?.valid) {
          const message = this.preFilled
            ? "Resident updated successfully!"
            : "Resident created successfully!";
          this.openSnackBar(message, "Okay");
          this.resetForm();
          this.showFormTabs = false;
          this.preFilled = false;
          this.dataSource.data = [];
        } else {
          this.openSnackBar(response?.message || "An error occurred.", "Okay");
          this.dataSource.data = [];
        }
      },
      error: (error: any) => {
        const errorMessage = error?.error?.message || (this.preFilled ? "Failed to update resident. Please try again." : "Failed to create resident. Please try again.");
        if (errorMessage && typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('password')) {
          this.loginForm.get('password')?.setErrors({ backendError: errorMessage });
          this.openSnackBar(errorMessage, "Okay");
        } else {
          this.openSnackBar(errorMessage, "Okay");
        }
      }
    });
  }

  checkUsernameExists(): void {
    if (this.preFilled) {
      const currentUsername = this.loginForm.get('userName')?.value;
      const originalUsername = this.originalUsername;

      if (currentUsername === originalUsername) {
        this.onSubmit();
        return;
      }
    }
    const userName = this.loginForm.get('userName')?.value;
    console.log('User Name:', userName);
    const userNameControl = this.loginForm.get('userName');
    const token: string | null = localStorage.getItem('accessToken');

    if (userName) {
      this.publicService.checkUsernameAvailability(userName, token || '').pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: any) => {
          this.userNameExists = response?.valid;
          if (this.userNameExists) {
            this.onSubmit();
          }
          else {
            userNameControl?.setErrors({ usernameExists: true });
            this.openSnackBar("Username already exists. Please choose a different username.", "Okay");
          }
          console.log('API response:', response);
          this.onUserSearch();
        },
        error: (error: unknown) => {
          console.error('API error:', error);
        }
      });
    }
  }

  onTabChange(event: { index: number }): void {
    const newTabIndex = event.index;
    const currentTabIndex = this.selectedTabIndex;

    // Check if the user is moving to the next tab
    if (newTabIndex > currentTabIndex) {
      let formToValidate: FormGroup;

      // Determine which form to validate based on the current tab
      switch (currentTabIndex) {
        case 0: // Current tab is the first one with residentForm
          formToValidate = this.residentForm;
          break;
        // Case 1 is skipped because the second tab form has no mandatory fields.
        // You can add logic here if it ever gets validation.
        // case 1:
        //   formToValidate = this.secondForm;
        //   break;
        case 2: // Current tab is the third one with loginForm
          formToValidate = this.loginForm;
          break;
        default:
          // No validation needed for other tabs, or handle as a fallback
          this.selectedTabIndex = newTabIndex;
          return;
      }

      // Perform validation check
      if (formToValidate && formToValidate.invalid) {
        // Prevent the tab change
        this.tabGroup.selectedIndex = currentTabIndex;

        // Mark all controls in the invalid form as touched to show errors
        formToValidate.markAllAsTouched();

        this.snackBar.open('Please fill out all required fields before proceeding.', 'OK', {
          duration: 3000,
        });
      } else {
        // Form is valid, allow the tab change
        this.selectedTabIndex = newTabIndex;
      }
    } else {
      // Allow moving backward without validation
      this.selectedTabIndex = newTabIndex;
    }
  }



  onUserSearch(): void {
    this.isLoading = true;

    const getValue = (value: unknown): string | null => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'string') return value === '' ? null : value;
      if (typeof value === 'number') return String(value);
      try {
        const s = (value as any)?.toString?.();
        return s === '' ? null : s;
      } catch {
        return null;
      }
    };

    const offset = this.pageIndex * this.pageSize;
    const limit = this.pageSize;

    const postData: ResidentSearchRequest = {
      residentName: getValue(this.searchForm.value.residentName),
      mobileNumber: getValue(this.searchForm.value.mobileNumber),
      emailAddress: getValue(this.searchForm.value.email),
      areaId: getValue(this.searchForm.value.areaId),
      qtrNo: getValue(this.searchForm.value.qtrNumber),
      blockNo: getValue(this.searchForm.value.blockNumber),
      offset: offset,
      limit: limit
    };

    this.ManageResidentsService.getSearchResults(postData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const data: LocationItem[] = response?.data || [];
          this.searchResults = data;
          this.dataSource.data = this.searchResults;

          console.log('Backend totalCount:', response?.totalCount);
          console.log('Search paginator length before:', this.searchLength);
          this.searchLength = response?.totalCount ?? this.searchLength;
          console.log('Search paginator length after:', this.searchLength);

          this.isLoading = false;

          if (this.paginator) {
            this.paginator.length = this.searchLength;
            this.paginator.pageIndex = this.pageIndex;
            this.paginator.pageSize = this.pageSize;
            this.cdr.detectChanges();
          }
        },
        error: (error: unknown) => {
          console.error('Resident search failed:', error);
          this.dataSource.data = [];
          this.searchLength = 0;
          this.isLoading = false;
          if (this.paginator) {
            this.paginator.length = this.searchLength;
            this.cdr.detectChanges();
          }
        }
      });
  }

  // setupPaginator: Make it reusable for any paginator
  private setupPaginator(paginator: MatPaginator | null, length: number): void {
    if (paginator) {
      paginator._intl.getRangeLabel = (page: number, pageSize: number, totalLength: number) => {
        if (totalLength === 0 || pageSize === 0) {
          return `0 of ${totalLength}`;
        }
        const startIndex = page * pageSize + 1;
        const endIndex = Math.min((page + 1) * pageSize, totalLength);
        return `${startIndex} – ${endIndex} of ${totalLength}`;
      };
      paginator.length = length; // Set the length for the paginator
    }
  }

  // onCancel: Reset assignedLocations and its paginator
  onCancel(): void {
    this.showFormTabs = false;
    this.assignedLocations.data = [];
    this.assignedLength = 0; // Reset length for assignedLocations
    this.isLoading = false;

    // Setup paginator for assignedLocations
    this.setupPaginator(this.assignedPaginator, this.assignedLength);
    this.cdr.detectChanges();
  }

  // ngAfterViewInit: Initialize both paginators
  ngAfterViewInit(): void {
    this.setupPaginator(this.paginator, this.searchLength);
    this.setupPaginator(this.assignedPaginator, this.assignedLength);
    this.assignedLocations.paginator = this.assignedPaginator;
    this.cdr.detectChanges();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.onUserSearch();
  }

  basicInfo(element: LocationItem, tab: string): void {
    this.preFilled = true;
    this.fillFormWithResidentData(element);
    this.updatePasswordValidation();
    // residentType might not exist on LocationItem; keep as string if present
    this.selectedResidentType = (element as any).residentType || this.selectedResidentType;
    console.log('Selected Resident Type:', this.selectedResidentType, 'Element:', element);
    this.showFormTabs = true;
    this.residentID = (element as any).residentId || this.residentID;
    const passwordControl = this.loginForm.get('password');
    if (passwordControl) {
      passwordControl.clearValidators();
      passwordControl.updateValueAndValidity(); // This is crucial
    }

    // Set the selected tab
    switch (tab) {
      case 'basicInfo':
        this.selectedTabIndex = 0;
        break;
      case 'manageLocation':
        this.selectedTabIndex = 1;
        break;
      case 'manageLogin':
        this.selectedTabIndex = 2;
        break;
    }
  }



  fillFormWithResidentData(resident: any) {
    this.assignedLocations.data = [];
    this.assignedLocationsArray = [];

    this.residentForm.patchValue({
      residentType: resident.residentType,
      residentPriority: resident.residentPriority,
      firstName: resident.residentFirstName,
      lastName: resident.residentLastName,
      rank: resident.rank,
      mobileNo: resident.mobileNumber,
      altermobileNo: resident.alternateMobileNumber,
      emailAddress: resident.emailAddress,
      allowEmail: resident.allowEmailFlag === 'Y' ? true : false,
      allowSMS: resident.allowSmsFlag === 'Y' ? true : false,
      residentId: resident.residentId
    });
    this.previousResidentType = resident.residentType;

    this.loginForm.patchValue({
      userName: resident.userName,
      password: '',
      loginAccountEnabled: resident.loginEnabled === 'Y' ? true : false
    });
    this.originalUsername = resident.userName;

    if (resident.locationAllocationList && resident.locationAllocationList.length > 0) {
      console.log('Resident Location Allocation List:', resident.locationAllocationList);

      this.assignedLocationsArray = resident.locationAllocationList
        .filter((location: any) => location.allocationId !== null) // Filter out invalid locations
        .map((location: any) => ({
          areaName: location.areaName,
          quarterNumber: location.quarterNumber,
          blockNumber: location.blockNumber,
          locationName: location.locationName,
          locationAssignedTo: resident.residentFirstName + ' ' + resident.residentLastName,
          id: location.allocationId,
          allocationId: location.allocationId,
          locationId: location.locationId,
          status: 'Assigned'
        } as AssignedLocation));
    }

    this.assignedLocations.data = [...this.assignedLocationsArray];
    // Use the dedicated assigned locations paginator if available
    if (this.assignedPaginator) {
      this.assignedLocations.paginator = this.assignedPaginator;
    }

    this.residentForm.get('firstName')?.markAsTouched();
    this.residentForm.get('lastName')?.markAsTouched();
    this.residentForm.get('rank')?.markAsTouched();

    console.log('Filled Assigned Locations:', this.assignedLocations.data);

  }

  private resetForm(): void {
    this.preFilled = false;
    this.residentForm.reset({
      residentType: 'IND',
      residentPriority: 'GENERAL',
      rank: '',
      mobileNo: '',
      firstName: '',
      emailAddress: '',
      lastName: '',
      allowEmail: true,
      allowSMS: true,
    });
    this.loginForm.reset({
      userName: '',
      password: '',
      loginAccountEnabled: true
    });

    this.assignedLocations.data = [];

    this.residentForm.markAsUntouched();
    this.residentForm.markAsPristine();
    this.updatePasswordValidation();
  }

  getUserZoneAreas() {
    this.UserManagementService.getUserAreas().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.areas = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  updatePasswordValidation(): void {
    const passwordControl = this.loginForm.get('password');
    if (passwordControl) {
      if (this.preFilled) {
        // this.residentForm.get('userName')?.disable();

        // In edit mode, clear the validators
        passwordControl.clearValidators();
      } else {
        // In new entry mode, set the validators for required, minLength, and maxLength
        passwordControl.setValidators([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(12)
        ]);
      }
      // Re-evaluate the control's validation status
      passwordControl.updateValueAndValidity();
    }
    if (this.preFilled) {
      this.loginForm.get('userName')?.disable();
    } else {
      this.loginForm.get('userName')?.enable();
    }
    console.log(this.preFilled);
  }

  onRemoveSpl(event: KeyboardEvent, formControlName: string) {
    const key = event.key;

    // Allow control keys (backspace, delete, arrows, tab, etc.)
    if (key.length > 1) return;

    // Only allow alphabets and spaces
    if (!/^[a-zA-Z\s]$/.test(key)) {
      event.preventDefault(); // block invalid char
      // don't setErrors here, let validator handle value-level validation
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
